import React, { useState, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;

/**
 * StepImageInput — add an image to an assembly step by drag-and-drop, file
 * picker, or (on a phone) the camera / photo library. Uploads to Cloudinary via
 * POST /quality/assembly-instructions/:id/step-image and stores the returned URL.
 * A plain URL field remains as a fallback.
 *
 * props: { instructionId, value, onChange(url), getToken, disabled, accent }
 */
export default function StepImageInput({ instructionId, value, onChange, getToken, disabled, accent = '#2dd4bf' }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('image', file);
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions/${instructionId || 'new'}/step-image`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (data.ok && data.url) onChange(data.url);
      else setErr(data.error || 'Upload failed');
    } catch (e) { setErr(e.message || 'Upload failed'); } finally { setUploading(false); }
  };

  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.05em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const btn = (extra) => ({ background:accent+'18',color:accent,border:`1px solid ${accent}44`,
    borderRadius:6,padding:'5px 12px',fontSize:12,fontWeight:700,cursor:'pointer',...extra });

  // Read-only (not editing): show the image if present, else a dash.
  if (disabled) {
    return (
      <div>
        <div style={labelS}>Image</div>
        {value
          ? <img src={value} alt="step" style={{ maxWidth:'100%',maxHeight:160,borderRadius:6,border:'1px solid var(--ni-border-soft)' }} />
          : <span style={{ fontSize:13,color:'var(--ni-text-muted)' }}>—</span>}
      </div>
    );
  }

  return (
    <div>
      <div style={labelS}>Image</div>

      {value ? (
        <div style={{ position:'relative',display:'inline-block' }}>
          <img src={value} alt="step" style={{ maxWidth:'100%',maxHeight:160,borderRadius:6,border:'1px solid var(--ni-border-soft)',display:'block' }} />
          <div style={{ display:'flex',gap:8,marginTop:6 }}>
            <button type="button" onClick={()=>fileRef.current && fileRef.current.click()} style={btn()}>Replace</button>
            <button type="button" onClick={()=>onChange('')}
              style={{ background:'rgba(236,33,36,0.12)',color:'#EC2124',border:'1px solid rgba(236,33,36,0.3)',
                borderRadius:6,padding:'5px 12px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Remove</button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={e=>{ e.preventDefault(); setDragOver(true); }}
          onDragLeave={()=>setDragOver(false)}
          onDrop={e=>{ e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
          style={{ border:`2px dashed ${dragOver?accent:'var(--ni-border-soft)'}`,borderRadius:8,padding:'14px',
            textAlign:'center',background:dragOver?accent+'11':'transparent',transition:'all 0.15s' }}>
          <div style={{ fontSize:12,color:'var(--ni-text-muted)',marginBottom:8 }}>
            {uploading ? 'Uploading…' : 'Drop an image here, or'}
          </div>
          <div style={{ display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap' }}>
            <button type="button" onClick={()=>fileRef.current && fileRef.current.click()} disabled={uploading} style={btn()}>Choose file</button>
            <button type="button" onClick={()=>camRef.current && camRef.current.click()} disabled={uploading} style={btn()}>📷 Take photo</button>
          </div>
        </div>
      )}

      {err && <div style={{ color:'#EC2124',fontSize:11,marginTop:6 }}>{err}</div>}

      {/* URL fallback (paste a hosted image link) */}
      <input value={value || ''} onChange={e=>onChange(e.target.value)} placeholder="…or paste an image URL"
        style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
          borderRadius:6,padding:'6px 10px',fontSize:12,width:'100%',boxSizing:'border-box',marginTop:8 }} />

      <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
        onChange={e=>{ upload(e.target.files?.[0]); e.target.value=''; }} />
      {/* capture hints mobile browsers to open the camera */}
      <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }}
        onChange={e=>{ upload(e.target.files?.[0]); e.target.value=''; }} />
    </div>
  );
}
